import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { orderApi } from '@formosa/shared/api/order';
import { ApiError } from '@formosa/shared/api/request';
import { formatThumbChange, formatThumbPrice, normalizeSymbolCode } from '@formosa/shared/utils/marketThumb';
import { useAuth } from '../../auth/AuthContext';
import { useWallet } from '../../context/WalletContext';
import useProductSearch from '../../context/ProductSearchContext';
import ContinueOrderModal from '../../components/trade/ContinueOrderModal';
import FuturesCandlestickChart from '../../components/trade/FuturesCandlestickChart';
import OrderSuccessModal from '../../components/trade/OrderSuccessModal';
import TimedOrderModal from '../../components/trade/TimedOrderModal';
import TimedPositionCard from '../../components/trade/TimedPositionCard';
import useMarketChart from '../../hooks/useMarketChart';
import useMarketThumbSocket from '../../hooks/useMarketThumbSocket';
import useRequireAuth from '../../hooks/useRequireAuth';
import { useTranslation } from '../../i18n/LocaleContext';
import {
  formatPrice,
  isTimedOrderPending,
  listDurationOptions,
  pickTimedProductBySymbol,
} from '../../utils/timedPlayConfig';

export default function TimedTradePage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const symbolParam = searchParams.get('symbol');
  const { isAuthenticated } = useAuth();
  const { refreshWallet } = useWallet();
  const requireAuth = useRequireAuth();
  const { openSearch } = useProductSearch();
  const { t } = useTranslation();

  const [products, setProducts] = useState([]);
  const [primaryProduct, setPrimaryProduct] = useState(null);
  const [positions, setPositions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [orderDirection, setOrderDirection] = useState('UP');
  const [orderModalOpen, setOrderModalOpen] = useState(false);
  const [selectedProductId, setSelectedProductId] = useState('');
  const [orderAmount, setOrderAmount] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const [successOpen, setSuccessOpen] = useState(false);
  const [continueOpen, setContinueOpen] = useState(false);
  const [activeOrder, setActiveOrder] = useState(null);

  const symbolCode = primaryProduct?.marketSymbolCode || '';
  const thumbMap = useMarketThumbSocket(symbolCode ? [symbolCode] : []);
  const thumb = symbolCode ? thumbMap[normalizeSymbolCode(symbolCode)] : null;
  const { bars, loading: chartLoading } = useMarketChart(symbolCode, {
    enabled: Boolean(symbolCode),
  });

  const durationOptions = useMemo(
    () => listDurationOptions(products, primaryProduct?.marketSymbolCode),
    [products, primaryProduct?.marketSymbolCode]
  );

  const priceChange = useMemo(() => formatThumbChange(thumb), [thumb]);

  const visiblePositions = useMemo(() => {
    if (!symbolCode) {
      return positions;
    }
    const normalized = String(symbolCode).trim().toUpperCase();
    return positions.filter(
      (item) => String(item.marketSymbolCode || '').trim().toUpperCase() === normalized
    );
  }, [positions, symbolCode]);

  const hasPendingPositions = useMemo(
    () => visiblePositions.some((item) => isTimedOrderPending(item)),
    [visiblePositions]
  );

  function resolvePositionProduct(order) {
    if (!order?.productId) {
      return null;
    }
    return products.find((item) => item.id === order.productId) || null;
  }

  const loadProducts = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const list = await orderApi.listTimedProducts();
      const rows = Array.isArray(list) ? list : [];
      setProducts(rows);
      const primary = pickTimedProductBySymbol(rows, symbolParam);
      setPrimaryProduct(primary);
      if (primary) {
        const options = listDurationOptions(rows, primary.marketSymbolCode);
        setSelectedProductId(String(options[0]?.productId || primary.id));
      }
    } catch (err) {
      setProducts([]);
      setPrimaryProduct(null);
      setError(err instanceof ApiError ? err.message : t('trade.loadProductsFailed'));
    } finally {
      setLoading(false);
    }
  }, [symbolParam, t]);

  const loadPositions = useCallback(async () => {
    if (!isAuthenticated) {
      setPositions([]);
      return;
    }
    try {
      const page = await orderApi.listTimedOrders({
        pageNo: 1,
        pageSize: 20,
        status: 'START,PROCESS',
      });
      setPositions(page?.records || []);
    } catch {
      setPositions([]);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    loadProducts();
  }, [loadProducts]);

  useEffect(() => {
    loadPositions();
  }, [loadPositions]);

  useEffect(() => {
    if (!isAuthenticated) {
      return undefined;
    }
    const intervalMs = hasPendingPositions ? 1000 : 10000;
    const timer = setInterval(() => {
      loadPositions();
    }, intervalMs);
    return () => clearInterval(timer);
  }, [isAuthenticated, hasPendingPositions, loadPositions]);

  function openOrderModal(direction) {
    if (!isAuthenticated) {
      const returnPath = symbolParam
        ? `/trade/timed?symbol=${encodeURIComponent(symbolParam)}`
        : '/trade/timed';
      requireAuth(returnPath);
      return;
    }
    setOrderDirection(direction);
    setOrderAmount('');
    if (durationOptions[0]) {
      setSelectedProductId(String(durationOptions[0].productId));
    }
    setError('');
    setOrderModalOpen(true);
  }

  async function handleConfirmOrder(payload = {}) {
    const productId = payload.productId || Number(selectedProductId);
    if (!productId || !orderAmount || Number(orderAmount) <= 0) {
      return;
    }
    if (!isAuthenticated) {
      requireAuth('/trade/timed');
      return;
    }

    setSubmitting(true);
    setError('');
    try {
      const result = await orderApi.placeTimedOrder({
        productId,
        orderTrend: orderDirection,
        betAmount: Number(orderAmount),
      });
      setOrderModalOpen(false);
      setActiveOrder(result);
      setSuccessOpen(true);
      refreshWallet();
      loadPositions();

      window.setTimeout(() => {
        setSuccessOpen(false);
        setContinueOpen(true);
      }, 1000);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('trade.placeOrderFailed'));
    } finally {
      setSubmitting(false);
    }
  }

  const displayCode = primaryProduct?.displayName || symbolCode || t('common.dash');
  const pairLabel = primaryProduct?.name || `${symbolCode}/USDT`;
  const volumeLabel = thumb?.volume != null ? formatPrice(thumb.volume, 0) : t('common.dash');

  return (
    <div className="timed-trade-page">
      <header className="timed-trade-page__header">
        <button
          type="button"
          className="timed-trade-page__back"
          aria-label={t('common.back')}
          onClick={() => navigate(-1)}
        >
          ‹
        </button>
        <h1>{pairLabel}</h1>
        <button
          type="button"
          className="timed-trade-page__search"
          aria-label={t('common.search')}
          onClick={openSearch}
        >
          ⌕
        </button>
      </header>

      {error && <div className="timed-trade-page__error">{error}</div>}
      {loading && <p className="timed-trade-page__status">{t('common.loading')}</p>}

      {!loading && primaryProduct && (
        <>
          <section className="timed-trade-page__quote">
            <div className="timed-trade-page__symbol">{displayCode}</div>
            <div className="timed-trade-page__price">{formatThumbPrice(thumb?.price, 4)}</div>
            <div className={`timed-trade-page__change ${priceChange.positive ? 'is-up' : 'is-down'}`}>
              {priceChange.text}
            </div>
            <div className="timed-trade-page__stats">
              <div>
                <span>最高</span>
                <strong>{formatPrice(thumb?.high, 2)}</strong>
              </div>
              <div>
                <span>最低</span>
                <strong>{formatPrice(thumb?.low, 2)}</strong>
              </div>
              <div>
                <span>成交量</span>
                <strong>{volumeLabel}</strong>
              </div>
            </div>
          </section>

          <section className="timed-trade-page__chart" aria-label={t('trade.chartAria')}>
            <FuturesCandlestickChart
              bars={bars}
              currentPrice={thumb?.price ?? thumb?.close}
              loading={chartLoading}
              height={200}
            />
          </section>

          <section className="timed-trade-page__positions">
            <div className="timed-trade-page__positions-head">
              <h2>{t('trade.positions')}</h2>
            </div>
            {visiblePositions.length === 0 ? (
              <div className="timed-trade-page__empty">
                <div aria-hidden>📄</div>
                <p>{t('common.noData')}</p>
              </div>
            ) : (
              <ul className="futures-trade-page__position-list">
                {visiblePositions.slice(0, 5).map((item) => (
                  <TimedPositionCard
                    key={item.id || item.orderSerialId}
                    order={item}
                    product={resolvePositionProduct(item)}
                    currentPrice={thumb?.price ?? thumb?.close}
                    onSettled={loadPositions}
                  />
                ))}
              </ul>
            )}
          </section>
        </>
      )}

      <div className="timed-trade-page__actions">
        <button type="button" className="timed-trade-page__buy is-up" onClick={() => openOrderModal('UP')}>
          {t('trade.buyUp')}
        </button>
        <button type="button" className="timed-trade-page__buy is-down" onClick={() => openOrderModal('DOWN')}>
          {t('trade.buyDown')}
        </button>
      </div>

      <TimedOrderModal
        open={orderModalOpen}
        direction={orderDirection}
        products={products}
        primaryProduct={primaryProduct}
        currentPrice={thumb?.price}
        selectedProductId={selectedProductId}
        amount={orderAmount}
        submitting={submitting}
        onClose={() => setOrderModalOpen(false)}
        onSelectProduct={setSelectedProductId}
        onAmountChange={setOrderAmount}
        onConfirm={handleConfirmOrder}
      />

      <OrderSuccessModal open={successOpen} onClose={() => setSuccessOpen(false)} />

      <ContinueOrderModal
        open={continueOpen}
        order={activeOrder}
        currentPrice={thumb?.price}
        onClose={() => setContinueOpen(false)}
        onContinue={() => {
          setContinueOpen(false);
          openOrderModal(orderDirection);
        }}
      />
    </div>
  );
}
